/**
 * DIV.1 + PPLX.4 — Mini-Badge für die Watchlist-Übersicht.
 *
 * Zeigt für Aktien/ETFs mit Dividenden-Daten:
 *   - Tage bis zum nächsten ex-Date (wenn angekündigt)
 *   - Letzte Auszahlung als Fallback
 *   - TTM-Yield wenn currentPrice da
 *
 * PPLX.4: Wenn Provider-Daten leer sind, fällt das Badge auf KI-recherchierte
 * Werte zurück (ai_dividend_yield_pct, dividend_years_increased aus
 * v5_ai_research_cache). Ein violetter Punkt markiert KI-only-Daten.
 *
 * W67 Phase 5.5 (2026-05-25): tooltip jetzt als <HoverTooltip> styled.
 */

import { Coins, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { daysToExDate, type DividendSummary } from '@/lib/dividends';
import type { AIResearchData } from '@/lib/ai-research';
import { HoverTooltip } from '@/components/ui/hover-tooltip';

interface Props {
  summary: DividendSummary | null | undefined;
  currentPrice?: number;
  compact?: boolean;
  /** PPLX.4: KI-Daten als Fallback wenn provider summary fehlt. */
  aiResearch?: AIResearchData | null;
}

export function DividendBadge({ summary, currentPrice, compact = false, aiResearch }: Props) {
  // Provider-Daten haben Vorrang
  if (summary) {
    const days = daysToExDate(summary);
    const upcoming = days !== null && days >= 0;
    const ttmYield =
      summary.ttm_amount !== null && currentPrice && currentPrice > 0
        ? (summary.ttm_amount / currentPrice) * 100
        : null;

    const headline = upcoming
      ? days === 0
        ? 'heute ex'
        : `in ${days} T`
      : `${summary.next_or_last_amount.toFixed(2)} ${summary.currency ?? ''}`;

    return (
      <HoverTooltip
        widthRem={20}
        content={
          <div className="space-y-1">
            <div className="font-semibold text-zinc-100">
              {upcoming ? 'Naechste Dividende' : 'Letzte Dividende'}
            </div>
            <div className="text-[10px] text-zinc-300 font-mono">
              {summary.next_or_last_ex_date} ·{' '}
              {summary.next_or_last_amount.toFixed(4)}{' '}
              {summary.currency ?? ''}
            </div>
            {ttmYield !== null && (
              <div className="text-[10px] text-emerald-300 font-mono">
                TTM-Yield {ttmYield.toFixed(2)} %
              </div>
            )}
            <div className="text-[9px] text-zinc-500 italic leading-tight">
              Provider-Daten (Finnhub/Tiingo)
            </div>
          </div>
        }
      >
        <div
          className={cn(
            // W81 Mobile-Fix — Touch-Target via min-h-[24px] auf Mobile.
            'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] min-h-[24px] sm:min-h-0 font-mono shrink-0',
            upcoming
              ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-200'
              : 'border-zinc-700/50 bg-zinc-900/40 text-zinc-300'
          )}
        >
          <Coins className="size-2.5 shrink-0" />
          {compact ? (
            <span>{ttmYield !== null ? `${ttmYield.toFixed(1)}%` : headline}</span>
          ) : (
            <span>
              {ttmYield !== null ? `${ttmYield.toFixed(1)} %` : 'Div'}
              <span className="text-zinc-500 ml-1">· {headline}</span>
            </span>
          )}
        </div>
      </HoverTooltip>
    );
  }

  // PPLX.4: KI-Fallback
  if (aiResearch) {
    const yieldPct = aiResearch.ai_dividend_yield_pct;
    const years = aiResearch.dividend_years_increased;
    const hasYield = yieldPct !== null && yieldPct !== undefined && yieldPct > 0;
    const hasYears = years !== null && years !== undefined && years > 0;
    if (!hasYield && !hasYears) return null;

    const aristokrat = hasYears && years >= 25;

    return (
      <HoverTooltip
        widthRem={20}
        content={
          <div className="space-y-1">
            <div className="font-semibold text-zinc-100">
              KI-recherchierte Dividende
            </div>
            {hasYield && (
              <div className="text-[10px] text-zinc-300 font-mono">
                Yield {yieldPct.toFixed(2)} %
              </div>
            )}
            {hasYears && (
              <div className="text-[10px] text-zinc-300 font-mono">
                {years} Jahre Erhoehung in Folge
                {aristokrat && <span className="text-emerald-300"> — Aristokrat</span>}
              </div>
            )}
            <div className="text-[9px] text-zinc-500 italic leading-tight">
              Quelle: Perplexity (Provider-Daten leer/nicht vorhanden)
            </div>
          </div>
        }
      >
        <div
          className={cn(
            'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-mono shrink-0',
            aristokrat
              ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-200'
              : 'border-violet-700/30 bg-violet-950/20 text-violet-200'
          )}
        >
          <Coins className="size-2.5 shrink-0" />
          <Sparkles className="size-2.5 text-violet-300 shrink-0" />
          {compact ? (
            <span>
              {hasYield ? `${yieldPct.toFixed(1)}%` : hasYears ? `${years}J` : 'Div'}
            </span>
          ) : (
            <span>
              {hasYield ? `${yieldPct.toFixed(1)} %` : 'Div'}
              {hasYears && (
                <span className="text-zinc-500 ml-1">
                  · {years}J{aristokrat ? ' ★' : ''}
                </span>
              )}
            </span>
          )}
        </div>
      </HoverTooltip>
    );
  }

  return null;
}
